// « Journée » (design 4), for MyGaz and for the agency: one line per rider
// from 7 h to 21 h, when they were online (0052), each delivery, the order
// under way, a kit install, a cancellation, and the cash they carry.
import { S } from '../../lib/strings';
import { fmtN, hhmm } from '../../lib/format';
import { dayPos, laneCash, signedN, hm } from '../../lib/agences';
import type { Account, DayAgency, Lane } from '../../lib/types';
import { AccountPill, Av, Star } from './Bits';

const H0 = 7, H1 = 21;
const pct = (iso: string) => dayPos(iso, H0, H1) * 100 + '%';

function LaneRow({ l, now, onRider, openOrder, right }: { l: Lane; now: string; onRider?(id: string): void; openOrder?(id: string): void; right?: React.ReactNode }) {
  const st = l.state === 'invited' ? S.ag.rs.invited : l.state === 'suspended' ? S.ag.rs.suspended : l.open ? S.ag.rs.busy : l.live ? S.ag.rs.free : S.ag.rs.off;
  const tone = l.state === 'invited' ? 'violet' : l.open ? 'blue' : l.live ? 'green' : 'grey';
  const cash = laneCash(l);
  return (
    <div className="ag-lane">
      <button className="ag-lw" onClick={onRider ? () => onRider(l.id) : undefined} disabled={!onRider}>
        <Av name={l.name} size={32} />
        <span><b>{l.name}</b><span className="muted">{l.rating != null && <><Star s={11} /> {String(l.rating).replace('.', ',')} · </>}<i className={'ag-dot ' + tone} />{st}</span></span>
      </button>
      <div className="ag-track">
        {l.online.map((o, i) => <span key={i} className="ag-on" style={{ left: pct(o.from), width: `calc(${pct(o.to)} - ${pct(o.from)})` }} />)}
        {l.open?.kind === 'first_install' && l.open.window_from && l.open.window_to && (
          <button className="ag-inst" style={{ left: pct(l.open.window_from), width: `calc(${pct(l.open.window_to)} - ${pct(l.open.window_from)})` }} onClick={openOrder ? () => openOrder(l.open!.order_id) : undefined}>
            {S.ag.day.installing} {l.open.ref}
          </button>
        )}
        {l.open && l.open.kind !== 'first_install' && l.open.since && (
          <button className="ag-run" style={{ left: pct(l.open.since), width: `max(10px, calc(${pct(now)} - ${pct(l.open.since)}))` }} onClick={openOrder ? () => openOrder(l.open!.order_id) : undefined} title={l.open.ref}>
            <span>{l.open.ref}</span>
          </button>
        )}
        {l.done.map((d) => <button key={d.order_id} className="ag-dn" style={{ left: pct(d.at) }} title={`${d.ref} · ${hhmm(d.at)}`} aria-label={`${S.ag.day.done} ${d.ref} ${hhmm(d.at)}`} onClick={openOrder ? () => openOrder(d.order_id) : undefined} />)}
        {l.cancelled.map((c) => <span key={c.ref} className="ag-cx" style={{ left: pct(c.at) }} title={`${S.ag.day.cancelled} ${c.ref} · ${hhmm(c.at)}`}>×</span>)}
      </div>
      <div className="ag-lc">{right ?? <><b className="num">{fmtN(cash)}</b><span className="muted">{l.open?.kind === 'first_install' && !l.done.length ? S.ag.day.installing.toLowerCase() : S.ag.day.nDone(l.done.length)}</span></>}</div>
    </div>
  );
}

export function DayBoard({ agencies, now, accounts, onRider, openOrder, cashCol, right }: {
  agencies: DayAgency[]; now: string; accounts?: Record<string, Account>; onRider?(id: string): void; openOrder?(id: string): void;
  cashCol?: string; right?(l: Lane): React.ReactNode;
}) {
  const hours = Array.from({ length: H1 - H0 - 1 }, (_, i) => H0 + 1 + i);
  const nowPos = dayPos(now, H0, H1);
  const nowH = Number(new Date(now).toLocaleString('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Africa/Douala' }));
  return (
    <div className="card ag-tl">
      <div className="ag-hours">
        <span className="cap">{S.ag.day.rider}</span>
        <div className="ag-hl">
          {hours.map((h) => (Math.abs(h - nowH) < 1 && nowPos > 0 && nowPos < 1 ? null : <span key={h} style={{ left: ((h - H0) / (H1 - H0)) * 100 + '%' }}>{h} h</span>))}
          {nowPos > 0 && nowPos < 1 && <span className="now" style={{ left: nowPos * 100 + '%' }}>{hm(now)}</span>}
        </div>
        <span className="cap r">{cashCol ?? S.ag.day.cashCol}</span>
      </div>
      <div className="ag-lanes">
        {nowPos > 0 && nowPos < 1 && <i className="ag-nowline" style={{ left: `calc(var(--lane-l) + (100% - var(--lane-l) - var(--lane-r)) * ${nowPos})` }} />}
        {agencies.map((a) => (
          <div key={a.id}>
            {accounts && (
              <div className="ag-lg">
                <span><b>{a.name}</b> · <span className="muted">{S.ag.day.live(a.riders.filter((r) => r.live).length, a.riders.filter((r) => r.state !== 'suspended').length)}</span></span>
                {accounts[a.id] && <span className="muted num">{signedN(accounts[a.id].balance)} XAF <AccountPill a={accounts[a.id]} /></span>}
              </div>
            )}
            {a.riders.map((l) => <LaneRow key={l.id} l={l} now={now} onRider={onRider} openOrder={openOrder} right={right?.(l)} />)}
          </div>
        ))}
      </div>
      <div className="ag-leg">
        <span><i className="ag-on s" />{S.ag.day.online}</span><span><i className="ag-dn s" />{S.ag.day.done}</span><span><i className="ag-run s" />{S.ag.day.open}</span>
        <span><i className="ag-inst s" />{S.ag.day.installing}</span><span><i className="ag-cx s">×</i>{S.ag.day.cancelled}</span><span><i className="ag-nowline s" />{S.ag.day.now}</span>
      </div>
      {agencies.every((a) => a.riders.every((r) => !r.online.length)) && <p className="muted ag-small ag-note">{S.ag.day.noOnline}</p>}
    </div>
  );
}
